import {type RefObject, useCallback, useRef, useState} from 'react';
import type {
    JitsiConference,
    JitsiConnection,
    JitsiTrack,
    JitsiTrackState,
} from '../pages/stream/types/lib-jitsi-meet';
import {buildOptions, MONITOR_BOT_NAME, STUDIO_NAME, shouldReceiveByName} from '../utils/StreamingUtil.ts';
import useCenterStore from '../store/useCenterStore.ts';
import callApi from '../apis/callApi.ts';

interface MonitorConnectionReturn {
    connect: () => Promise<void>;
    leave: () => Promise<void>;
    remoteTracks: RefObject<Map<string, JitsiTrackState>>;
    remoteTrackVersion: number;
    isConnected: boolean;
}

const CONSTRAINT_DEBOUNCE_MS = 200;
const PROBE_SLOTS = 5;

/** center_id 기반 0~maxMs 안정 지연 (Streaming.tsx getStableJitter와 동일 규칙) */
const getStableJitter = (seed: string, maxMs: number): number => {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
        hash = (hash * 31 + seed.charCodeAt(i)) | 0;
    }
    return Math.abs(hash) % maxMs;
};

const useMonitorConnection = (): MonitorConnectionReturn => {
    const {centerInfo} = useCenterStore();
    const connectionRef = useRef<JitsiConnection | null>(null);
    const conferenceRef = useRef<JitsiConference | null>(null);
    const remoteTracks = useRef<Map<string, JitsiTrackState>>(new Map());
    const [remoteTrackVersion, setRemoteTrackVersion] = useState(0);
    const [isConnected, setIsConnected] = useState(false);
    const constraintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const jitterTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const joinTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // 봇 토큰 직접 발급 (localStorage 캐시 미사용)
    const fetchBotToken = useCallback(async (): Promise<string> => {
        const {data} = await callApi().post('jitsi/token/', {user: MONITOR_BOT_NAME});
        return data.token.slice(2, -1);
    }, []);

    // 자기 몫(홀) source만 receiver-constraints 발행 (디바운스)
    const publishConstraints = useCallback(() => {
        if (constraintTimerRef.current) clearTimeout(constraintTimerRef.current);
        constraintTimerRef.current = setTimeout(() => {
            const conf = conferenceRef.current;
            if (!conf) return;
            const selectedSources: string[] = [];
            for (const state of remoteTracks.current.values()) {
                if (state.videoTrack) selectedSources.push(state.videoTrack.getSourceName());
            }
            const lastN = Math.max(selectedSources.length, remoteTracks.current.size) + PROBE_SLOTS;
            conf.setReceiverConstraints({
                lastN,
                defaultConstraints: {maxHeight: 90, maxFrameRate: 10},
                selectedSources,
                constraints: {},
            });
            console.log(`🤖 [BOT_CONSTRAINTS] lastN=${lastN}, selected=${selectedSources.length}`);
        }, CONSTRAINT_DEBOUNCE_MS);
    }, []);

    const handleTrackAdded = useCallback((track: JitsiTrack) => {
        if (!track || typeof track.getType !== 'function') return;
        if (track.isLocal()) return; // 봇은 로컬 트랙 없음
        const participantId = track.getParticipantId();
        const participant = participantId ? track.conference?.participants.get(participantId) : null;
        const displayName = participant?.getDisplayName() || 'Unknown';
        // 봇/스튜디오 무시 + 홀 패리티만 수신
        if (displayName === MONITOR_BOT_NAME || displayName === STUDIO_NAME) return;
        if (!shouldReceiveByName(displayName, 'bot')) return;

        const trackType = track.getType();
        const found = remoteTracks.current.get(participantId!);
        if (!found) {
            const newState: JitsiTrackState = {partId: participantId!, videoTrack: null, audioTrack: null, displayName};
            if (trackType === 'video') newState.videoTrack = track;
            else newState.audioTrack = track;
            remoteTracks.current.set(participantId!, newState);
        } else {
            if (trackType === 'video') found.videoTrack = track;
            else found.audioTrack = track;
            found.displayName = displayName;
        }
        setRemoteTrackVersion((v) => v + 1);
        publishConstraints();
    }, [publishConstraints]);

    const handleTrackRemoved = useCallback((track: JitsiTrack) => {
        if (track.isLocal()) return;
        const participantId = track.getParticipantId();
        const trackType = track.getType();
        const found = remoteTracks.current.get(participantId);
        if (found) {
            if (trackType === 'video') found.videoTrack = null;
            else found.audioTrack = null;
            if (!found.videoTrack && !found.audioTrack) {
                remoteTracks.current.delete(participantId);
            }
            setRemoteTrackVersion((v) => v + 1);
        }
        track.dispose();
    }, []);

    const onConferenceJoined = useCallback(() => {
        setIsConnected(true);
        const conf = conferenceRef.current;
        if (conf) {
            conf.setReceiverConstraints({
                lastN: PROBE_SLOTS,
                defaultConstraints: {maxHeight: 90, maxFrameRate: 10},
            });
            joinTimerRef.current = setTimeout(() => publishConstraints(), 5000);
        }
        console.log('🤖 [BOT] CONFERENCE_JOINED');
    }, [publishConstraints]);

    const onConnectionSuccess = useCallback((connection: JitsiConnection) => {
        const room = import.meta.env.VITE_ROOM_NAME;
        const conference = connection.initJitsiConference(room, {
            userInfo: {displayName: MONITOR_BOT_NAME},
        });
        conferenceRef.current = conference;
        conference.on(window.JitsiMeetJS.events.conference.TRACK_ADDED, handleTrackAdded);
        conference.on(window.JitsiMeetJS.events.conference.TRACK_REMOVED, handleTrackRemoved);
        conference.on(window.JitsiMeetJS.events.conference.CONFERENCE_JOINED, onConferenceJoined);
        conference.setDisplayName(MONITOR_BOT_NAME);
        conference.join();
    }, [handleTrackAdded, handleTrackRemoved, onConferenceJoined]);

    const connect = useCallback(async () => {
        if (connectionRef.current) return;
        try {
            const jwt = await fetchBotToken();
            if (!jwt) {
                console.warn('🤖 [BOT] 토큰 발급 실패 — 봇 연결 생략, origin 절반만 표출');
                return;
            }
            const appId = import.meta.env.VITE_ICSC_APP_ID;
            const room = import.meta.env.VITE_ROOM_NAME;
            const options = buildOptions(appId, room);
            // 센터별 안정 jitter (thundering herd 방지)
            const jitterMs = getStableJitter(centerInfo?.center_id || '', 15000);
            await new Promise<void>((resolve) => {
                jitterTimerRef.current = setTimeout(resolve, jitterMs);
            });
            const connection = new window.JitsiMeetJS.JitsiConnection(null, jwt, options);
            connection.addEventListener(
                window.JitsiMeetJS.events.connection.CONNECTION_ESTABLISHED,
                () => onConnectionSuccess(connection)
            );
            connection.addEventListener(
                window.JitsiMeetJS.events.connection.CONNECTION_FAILED,
                (e: unknown) => console.warn('🤖 [BOT] CONNECTION_FAILED', e)
            );
            connectionRef.current = connection;
            connection.connect();
        } catch (e) {
            console.warn('🤖 [BOT] connect 실패 — origin만으로 계속:', e);
        }
    }, [fetchBotToken, centerInfo?.center_id, onConnectionSuccess]);

    const leave = useCallback(async () => {
        try {
            if (constraintTimerRef.current) clearTimeout(constraintTimerRef.current);
            if (jitterTimerRef.current) clearTimeout(jitterTimerRef.current);
            if (joinTimerRef.current) clearTimeout(joinTimerRef.current);
            const conf = conferenceRef.current;
            if (conf) await conf.leave();
            const conn = connectionRef.current;
            if (conn) conn.disconnect();
        } catch (e) {
            console.warn('🤖 [BOT] leave 실패:', e);
        } finally {
            conferenceRef.current = null;
            connectionRef.current = null;
            remoteTracks.current.clear();
            setIsConnected(false);
            setRemoteTrackVersion((v) => v + 1);
        }
    }, []);

    return {connect, leave, remoteTracks, remoteTrackVersion, isConnected};
};

export default useMonitorConnection;
